<template>
    <div class="totalReport wrapper" v-loading="pageLoading">
        <proTitle></proTitle>
        <div class="wizard-toolbar">
            <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回报告列表</el-button>
            <span class="mode-label">{{ modeLabel }}</span>
        </div>

        <el-steps :active="step - 1" finish-status="success" align-center class="wizard-steps">
            <el-step v-for="item in stepList" :key="item.no" :title="item.title" @click.native="onStepClick(item.no)" />
        </el-steps>

        <div v-show="step === 1" class="wizard-card">
            <div class="card-title">选择报告模板</div>
            <el-form label-width="96px" size="small" class="base-form" @submit.native.prevent>
                <el-form-item label="报告名称" required>
                    <el-input v-model.trim="form.name" maxlength="60" :disabled="configLocked"
                        placeholder="请输入报告名称，如：观思问报告-第3周" />
                </el-form-item>
                <el-form-item label="AI模板" required>
                    <el-select v-model="form.aiProvider" :disabled="configLocked" placeholder="请选择模型"
                        no-data-text="暂无可用模型" style="width: 240px">
                        <el-option v-for="item in aiOptions" :key="item.code" :label="item.name" :value="item.code" />
                    </el-select>
                </el-form-item>
                <el-form-item label="报告模板" required>
                    <div class="tpl-grid">
                        <div v-for="item in templates" :key="item.id" class="tpl-card"
                            :class="{ active: form.templateId === item.id, disabled: configLocked }"
                            @click="pickTemplate(item)">
                            <div class="tpl-name">{{ item.name }}</div>
                            <ul v-if="cardSections(item).length" class="tpl-sections">
                                <li v-for="(sec, index) in cardSections(item)" :key="index">{{ sec.title }}</li>
                            </ul>
                            <p v-else class="tpl-sec">暂无章节</p>
                            <div class="tpl-card-foot">
                                <span>点击卡片选中</span>
                                <el-button type="primary" size="small" @click.stop="openTemplateEditor(item)">
                                    {{ configLocked ? "查看" : "查看/编辑" }}
                                </el-button>
                            </div>
                        </div>
                        <el-empty v-if="!templates.length" description="暂无可用模板" />
                    </div>
                </el-form-item>
            </el-form>
            <div class="bottom-bar">
                <span></span>
                <el-button type="primary" :disabled="!canLeaveStep1" :loading="saving" @click="goNext">
                    下一步：选择数据范围
                </el-button>
            </div>
        </div>

        <div v-show="step === 2" class="wizard-card">
            <div class="card-title">选择数据范围</div>
            <el-alert v-if="scopeLocked" type="warning" :closable="false" show-icon
                title="处理中、生成中不能修改数据范围" style="margin-bottom: 16px" />
            <el-form label-width="96px" size="small" @submit.native.prevent>
                <el-form-item label="数据维度" required>
                    <div class="source-tabs">
                        <button v-for="item in sourceTabs" :key="item.code" type="button" class="source-tab"
                            :class="{ 'is-on': isSourceOn(item.code), 'is-focus': scopeForm.focusType === item.code }"
                            :disabled="configLocked" @click="onPickSource(item.code)">
                            {{ item.label }}
                        </button>
                    </div>
                    <p class="field-hint">可多选。点击已选中的维度可取消，至少保留一个。</p>
                </el-form-item>
                <el-form-item v-if="selectedSources.length" label="已选维度">
                    <div class="selected-chips">
                        <span v-for="code in selectedSources" :key="code" class="selected-chip"
                            :class="{ 'is-focus': scopeForm.focusType === code }" @click="focusSource(code)">
                            <b>{{ sourceLabel(code) }}</b>
                            <em>{{ sourceSummary(code) }}</em>
                        </span>
                    </div>
                </el-form-item>
                <el-form-item label="报告周期">
                    <div class="scope-checks">
                        <el-checkbox v-model="scopeForm.stageAll" :disabled="configLocked" @change="onToggleAll('stage')">全部</el-checkbox>
                        <el-checkbox-group v-model="scopeForm.stageIds" class="scope-items" :disabled="configLocked" @change="onToggleItem('stage')">
                            <el-checkbox v-for="item in scopeOptions.stages" :key="item.id" :label="item.id">{{ item.name }}</el-checkbox>
                        </el-checkbox-group>
                    </div>
                </el-form-item>
                <el-form-item label="用户组">
                    <div class="scope-checks">
                        <el-checkbox v-model="scopeForm.groupAll" :disabled="configLocked" @change="onToggleAll('group')">全部</el-checkbox>
                        <el-checkbox-group v-model="scopeForm.userGroupIds" class="scope-items" :disabled="configLocked" @change="onToggleItem('group')">
                            <el-checkbox v-for="item in scopeOptions.userGroups" :key="item.id" :label="item.id">{{ item.name }}</el-checkbox>
                        </el-checkbox-group>
                    </div>
                </el-form-item>
                <el-form-item label="区域范围">
                    <div class="scope-checks">
                        <el-checkbox v-model="scopeForm.areaAll" :disabled="configLocked" @change="onToggleAll('area')">全部</el-checkbox>
                        <el-checkbox-group v-model="scopeForm.areas" class="scope-items" :disabled="configLocked" @change="onToggleItem('area')">
                            <el-checkbox v-for="item in scopeOptions.areas" :key="item" :label="item">{{ item }}</el-checkbox>
                        </el-checkbox-group>
                    </div>
                </el-form-item>
                <template v-if="scopeForm.focusType === 'gsq'">
                    <el-form-item label="观思问">
                        <el-radio-group v-model="scopeForm.gsqMode" :disabled="configLocked">
                            <el-radio label="date">起止日期</el-radio>
                            <el-radio label="dates">选择问卷日期</el-radio>
                            <el-radio label="cycle">组合周期</el-radio>
                        </el-radio-group>
                    </el-form-item>
                    <el-form-item v-if="scopeForm.gsqMode === 'date'" label="问卷时间">
                        <el-date-picker v-model="scopeForm.dateStart" type="date" value-format="yyyy-MM-dd"
                            placeholder="开始日期" :disabled="configLocked" />
                        <span class="date-sep">至</span>
                        <el-date-picker v-model="scopeForm.dateEnd" type="date" value-format="yyyy-MM-dd"
                            placeholder="结束日期" :disabled="configLocked" />
                        <p class="field-hint">按观思问问卷时间筛选；区间含首尾全部日历天。排除周末请用「选择问卷日期」</p>
                    </el-form-item>
                    <el-form-item v-else-if="scopeForm.gsqMode === 'dates'" label="问卷日期">
                        <el-date-picker v-model="scopeForm.gsqDates" type="dates" value-format="yyyy-MM-dd"
                            placeholder="可多选日期" :disabled="configLocked" style="width: 320px" />
                        <p class="field-hint">按问卷时间逐日多选，不选周六日即可排除周末；作答天数与提交率按所选天数计算</p>
                    </el-form-item>
                    <el-form-item v-else label="周期">
                        <div class="scope-checks">
                            <el-checkbox v-model="scopeForm.cycleAll" :disabled="configLocked" @change="onToggleAll('cycle')">全部</el-checkbox>
                            <el-checkbox-group v-model="scopeForm.cycleIds" class="scope-items" :disabled="configLocked" @change="onToggleItem('cycle')">
                                <el-checkbox v-for="item in visibleCycles" :key="item.id" :label="item.id">{{ item.name }}</el-checkbox>
                            </el-checkbox-group>
                        </div>
                    </el-form-item>
                </template>
                <el-form-item v-if="scopeForm.focusType === 'homework'" label="作业">
                    <div class="scope-checks">
                        <el-checkbox v-model="scopeForm.homeworkAll" :disabled="configLocked" @change="onToggleAll('homework')">全部</el-checkbox>
                        <el-checkbox-group v-model="scopeForm.homeworkIds" class="scope-items" :disabled="configLocked" @change="onToggleItem('homework')">
                            <el-checkbox v-for="item in scopeOptions.homeworks" :key="item.id" :label="item.id">{{ item.name }}</el-checkbox>
                        </el-checkbox-group>
                    </div>
                </el-form-item>
                <template v-if="scopeForm.focusType === 'log'">
                    <el-form-item label="日志">
                        <el-radio-group v-model="scopeForm.logMode" :disabled="configLocked">
                            <el-radio label="date">起止日期</el-radio>
                            <el-radio label="dates">选择作答日期</el-radio>
                        </el-radio-group>
                    </el-form-item>
                    <el-form-item v-if="scopeForm.logMode !== 'dates'" label="日志日期">
                        <el-date-picker v-model="scopeForm.logStart" type="date" value-format="yyyy-MM-dd"
                            placeholder="开始日期" :disabled="configLocked" />
                        <span class="date-sep">至</span>
                        <el-date-picker v-model="scopeForm.logEnd" type="date" value-format="yyyy-MM-dd"
                            placeholder="结束日期" :disabled="configLocked" />
                        <p class="field-hint">按日志应作答日期筛选，不填则包含全部作答日；排除周末请用「选择作答日期」</p>
                    </el-form-item>
                    <el-form-item v-else label="作答日期">
                        <el-date-picker v-model="scopeForm.logDates" type="dates" value-format="yyyy-MM-dd"
                            placeholder="可多选日期" :disabled="configLocked" style="width: 320px" />
                        <p class="field-hint">逐日多选应作答日期，不选周六日即可排除周末</p>
                    </el-form-item>
                </template>
                <el-form-item v-if="scopeForm.focusType === 'survey'" label="调研问卷">
                    <el-checkbox-group v-if="scopeOptions.surveys.length" v-model="scopeForm.surveyIds"
                        class="list-checks" :disabled="configLocked">
                        <el-checkbox v-for="item in scopeOptions.surveys" :key="item.id" :label="item.id">
                            {{ item.name }}<span v-if="item.meta" class="check-meta">{{ item.meta }}</span>
                        </el-checkbox>
                    </el-checkbox-group>
                    <p v-else class="field-hint">本项目暂无调研问卷</p>
                </el-form-item>
                <el-form-item v-if="scopeForm.focusType === 'report'" label="引用报告">
                    <el-checkbox-group v-if="reportChoices.length" v-model="scopeForm.reportIds"
                        class="list-checks" :disabled="configLocked">
                        <el-checkbox v-for="item in reportChoices" :key="item.id" :label="item.id">
                            {{ item.name }}<span v-if="item.meta" class="check-meta">{{ item.meta }}</span>
                        </el-checkbox>
                    </el-checkbox-group>
                    <p v-else class="field-hint">本项目暂无其他已完成的报告</p>
                </el-form-item>
                <template v-if="scopeForm.focusType === 'attendance'">
                    <el-form-item label="考勤">
                        <el-radio-group v-model="scopeForm.attendanceMode" :disabled="configLocked">
                            <el-radio label="date">起止日期</el-radio>
                            <el-radio label="dates">选择考勤日期</el-radio>
                        </el-radio-group>
                    </el-form-item>
                    <el-form-item v-if="scopeForm.attendanceMode !== 'dates'" label="考勤日期">
                        <el-date-picker v-model="scopeForm.attendanceStart" type="date" value-format="yyyy-MM-dd"
                            placeholder="开始日期" :disabled="configLocked" />
                        <span class="date-sep">至</span>
                        <el-date-picker v-model="scopeForm.attendanceEnd" type="date" value-format="yyyy-MM-dd"
                            placeholder="结束日期" :disabled="configLocked" />
                        <p class="field-hint">不填则包含全部考勤日期；排除周末请用「选择考勤日期」</p>
                    </el-form-item>
                    <el-form-item v-else label="考勤日期">
                        <el-date-picker v-model="scopeForm.attendanceDates" type="dates" value-format="yyyy-MM-dd"
                            placeholder="可多选日期" :disabled="configLocked" style="width: 320px" />
                        <p class="field-hint">逐日多选考勤日期，不选周六日即可排除周末</p>
                    </el-form-item>
                </template>
            </el-form>
            <div class="bottom-bar">
                <el-button @click="step = 1">上一步</el-button>
                <el-button type="primary" :loading="saving" @click="goPrepare">下一步：数据处理</el-button>
            </div>
        </div>

        <div v-show="step === 3" class="wizard-card">
            <div class="card-title">
                数据处理
                <span class="card-hint">核对该范围内有没有可分析的数据。观思问正文不含周总结；未选考勤维度时也会自动附带对应阶段的考勤。核对通过后即可生成。</span>
            </div>
            <p class="field-hint prepare-scope">本次范围：{{ prepareScopeText }}</p>
            <div class="progress-head">
                <span>处理进度</span>
                <span>{{ prepareTask.percent || 0 }}%</span>
            </div>
            <el-progress :percentage="prepareTask.percent || 0" :status="progressStatus(prepareTask.status)" />
            <p class="progress-meta">已完成 {{ prepareTask.done || 0 }} / {{ prepareTask.total || 0 }} 项
                <template v-if="prepareTask.current"> · 当前：{{ prepareTask.current }}</template>
            </p>
            <el-alert v-if="prepareTask.errorMsg" :title="prepareTask.errorMsg" type="error" :closable="false" show-icon />
            <div class="log-panels">
                <div class="log-panel">
                    <div class="log-title">当前处理数据</div>
                    <div class="log-current">{{ prepareTask.current || "等待开始处理…" }}</div>
                </div>
                <div class="log-panel">
                    <div class="log-title">处理日志</div>
                    <ul v-if="prepareTask.logs.length" class="log-list">
                        <li v-for="item in prepareTask.logs" :key="item.id">
                            <el-tag size="mini" :type="logTag(item.status)">{{ logStatusText(item.status) }}</el-tag>
                            <span>{{ item.message || item.sectionTitle || item.targetName }}</span>
                            <span class="log-time">{{ item.time }}</span>
                        </li>
                    </ul>
                    <el-empty v-else description="暂无日志" :image-size="64" />
                </div>
            </div>
            <div class="bottom-bar">
                <el-button :disabled="prepareTask.status === 'running'" @click="step = 2">上一步</el-button>
                <div>
                    <el-button v-if="prepareTask.status === 'running' || prepareTask.status === 'pending'"
                        @click="stopTask('prepare')">停止</el-button>
                    <el-button v-if="prepareTask.status === 'stopped'" type="primary" plain
                        @click="continueTask('prepare')">继续处理</el-button>
                    <el-button v-if="prepareTask.status === 'failed' && !readonly" @click="startPrepare(true)">重新处理</el-button>
                    <el-button type="primary" :disabled="!canGenerate" :loading="generating" @click="startGenerate">
                        {{ generateBtnText }}
                    </el-button>
                </div>
            </div>
        </div>

        <div v-show="step === 4" class="wizard-card">
            <div class="card-title">
                报告预览
                <el-tag size="small">{{ currentTemplateName }}</el-tag>
            </div>
            <p v-if="scopeSourceText" class="field-hint source-meta">数据来源：{{ scopeSourceText }}</p>
            <div v-if="generateTask.status === 'running' || generateTask.status === 'pending'" class="gen-progress">
                <div class="progress-head">
                    <span>{{ form.category === 'personal' ? '个人报告生成中' : '报告生成中' }}</span>
                    <span>{{ generateTask.percent || 0 }}%</span>
                </div>
                <el-progress :percentage="generateTask.percent || 0" />
                <p class="progress-meta">{{ generateTask.current || "准备生成…" }}</p>
            </div>
            <el-alert v-if="generateTask.errorMsg" :title="generateTask.errorMsg" type="error" :closable="false"
                show-icon style="margin-bottom: 12px" />
            <div class="preview-toolbar">
                <span class="version-label">历史版本</span>
                <el-select v-model="versionId" size="small" placeholder="暂无版本" style="width: 220px"
                    @change="loadPreview">
                    <el-option v-for="item in versions" :key="item.id" :label="versionOptionLabel(item)" :value="item.id" />
                </el-select>
                <div class="preview-actions">
                    <el-button v-if="isGenerateBusy" size="small"
                        @click="stopTask('generate')">停止</el-button>
                    <el-button v-if="canContinueGenerate" size="small"
                        @click="continueGenerate()">继续生成</el-button>
                    <el-button v-if="!readonly" size="small" type="success" :disabled="!canEditContent"
                        :loading="contentSaving" @click="saveContent">保存</el-button>
                    <el-button v-if="!readonly" size="small" type="primary" :disabled="!versionId || generating"
                        @click="openAdjust()">报告调整</el-button>
                    <el-button v-if="!readonly" size="small" type="primary" :disabled="!versions.length"
                        @click="openPublish">发布</el-button>
                    <el-button size="small" type="warning" :disabled="!versionId || exporting" :loading="exporting"
                        @click="exportVisible = true">导出报告</el-button>
                </div>
            </div>
            <div class="preview-layout" :class="{ 'is-personal': form.category === 'personal' }">
                <aside v-if="form.category === 'personal'" class="member-side">
                    <div class="member-head">用户组成员 {{ members.length ? `（${members.length}）` : '' }}</div>
                    <div v-for="item in members" :key="item.userId" class="member-item"
                        :class="{ active: activeUserId === item.userId }" @click="switchMember(item.userId)">
                        <div class="member-main">
                            <div class="member-name-row">
                                <span class="member-name">{{ item.userName || ('用户' + item.userId) }}</span>
                                <el-tag size="mini" :type="genTag(item.genStatus)">{{ memberLabel(item) }}</el-tag>
                            </div>
                            <el-tooltip v-if="item.failReason" :content="item.failReason" placement="right">
                                <div class="fail-reason">{{ item.failReason }}</div>
                            </el-tooltip>
                        </div>
                        <el-button v-if="canRetryMember(item) && item.genStatus !== 'done'" type="text" size="mini" class="member-retry"
                            @click.stop="continueGenerate({ userId: item.userId })">继续</el-button>
                    </div>
                    <el-empty v-if="!members.length" description="暂无成员" :image-size="64" />
                </aside>
                <div class="preview-main" v-loading="previewLoading">
                    <div v-if="sections.length">
                        <div v-for="item in sections" :key="sectionKey(item)" class="section-block">
                            <div class="section-head">
                                <span>{{ item.sortNo }}. {{ item.title }}</span>
                                <el-tag v-if="item.genStatus" size="mini" :type="genTag(item.genStatus)">
                                    {{ item.genStatusLabel || item.genStatus }}
                                </el-tag>
                                <div class="section-ops">
                                    <el-button v-if="canRetrySection(item) && item.genStatus !== 'done'" type="text" size="mini"
                                        @click="continueGenerate(sectionRetryOpts(item))">继续</el-button>
                                    <el-button v-if="canEditContent" type="text" size="mini" @click="toggleContentSource(item)">
                                        {{ contentSourceOn(item) ? "预览" : "编辑正文" }}
                                    </el-button>
                                    <el-button v-if="canOpenAdjust" size="mini" type="primary" plain
                                        @click="openAdjust(item.sortNo)">调整本章</el-button>
                                </div>
                            </div>
                            <el-alert v-if="item.failReason" :title="item.failReason" type="error" :closable="false"
                                show-icon class="section-fail" />
                            <el-input v-if="contentSourceOn(item)" v-model="item.content" type="textarea" :rows="8"
                                resize="none" class="scroll-text" placeholder="章节正文，支持 Markdown" />
                            <div v-else class="scroll-text-view md-view" v-html="renderMarkdown(item.content)"></div>
                        </div>
                    </div>
                    <el-empty v-else description="暂无正文，请先完成生成" />
                </div>
            </div>
        </div>

        <el-dialog :title="configLocked ? '查看模板' : '编辑本报告模板'" :visible.sync="editorVisible" width="860px"
            top="6vh" custom-class="editor-dialog" @opened="resetEditorScroll">
            <el-form label-position="top" size="small" class="editor-form" @submit.native.prevent>
                <el-form-item label="总提示词">
                    <el-input v-model="editorForm.systemPrompt" type="textarea" :rows="6" resize="none"
                        class="scroll-text" :disabled="configLocked" placeholder="本报告使用的总提示词" />
                </el-form-item>
                <el-form-item v-if="showRule" label="输出约束">
                    <el-input v-model="editorForm.outputRule" type="textarea" :rows="4" resize="none"
                        class="scroll-text" :disabled="configLocked" placeholder="约束模型输出格式，默认不展示" />
                </el-form-item>
                <el-form-item label="章节">
                    <div v-for="(item, index) in editorForm.sections" :key="index" class="editor-section">
                        <div class="editor-section-head">
                            <span>第 {{ index + 1 }} 章</span>
                            <el-button v-if="!configLocked" type="text" @click="removeEditorSection(index)">删除</el-button>
                        </div>
                        <div class="prompt-row">
                            <div class="prompt-label">章节标题</div>
                            <el-input v-model.trim="item.title" maxlength="80" :disabled="configLocked" placeholder="请输入章节标题" />
                        </div>
                        <div class="prompt-row prompt-row-top">
                            <div class="prompt-label">章节提示词</div>
                            <el-input v-model="item.content" type="textarea" :autosize="{ minRows: 2 }" resize="none"
                                class="editor-prompt" :disabled="configLocked" placeholder="请输入章节提示词" />
                        </div>
                    </div>
                    <el-button v-if="!configLocked" size="mini" icon="el-icon-plus" @click="addEditorSection">添加章节</el-button>
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="editorVisible = false">{{ configLocked ? "关闭" : "取消" }}</el-button>
                <el-button v-if="!configLocked" type="primary" :loading="saving" @click="saveTemplateEditor">保存</el-button>
            </span>
        </el-dialog>

        <el-dialog title="报告调整" :visible.sync="adjustVisible" width="800px" top="6vh" custom-class="adjust-dialog"
            @opened="fitPromptHeight">
            <div class="adjust-body">
                <div class="adjust-meta">
                    <div>
                        <div class="adjust-label">历史版本</div>
                        <div class="adjust-version-row">
                            <el-select v-model="adjustForm.versionId" size="small" @change="loadAdjustSections">
                                <el-option v-for="item in versions" :key="item.id" :label="versionOptionLabel(item)" :value="item.id" />
                            </el-select>
                            <el-button size="small" type="danger" plain :loading="deletingVersion" @click="deleteAdjustVersion">删除版本</el-button>
                        </div>
                    </div>
                    <div>
                        <div class="adjust-label">AI模型</div>
                        <el-select v-model="adjustForm.aiProvider" size="small" placeholder="请选择模型" no-data-text="暂无可用模型"
                            style="width: 100%">
                            <el-option v-for="item in aiOptions" :key="item.code" :label="item.name" :value="item.code" />
                        </el-select>
                    </div>
                    <div>
                        <div class="adjust-label">本版说明</div>
                        <el-input v-model.trim="adjustForm.note" size="small" maxlength="80" placeholder="如：修订共性需求分析表述" />
                    </div>
                </div>
                <div class="adjust-block">
                    <div class="adjust-label">总提示词</div>
                    <el-input v-model="adjustForm.systemPrompt" type="textarea" :rows="5" resize="none"
                        class="scroll-text" placeholder="本报告使用的总提示词" />
                </div>
                <div v-if="showRule" class="adjust-block">
                    <div class="adjust-label">输出约束</div>
                    <el-input v-model="adjustForm.outputRule" type="textarea" :rows="4" resize="none"
                        class="scroll-text" placeholder="约束模型输出格式" />
                </div>
                <div class="adjust-block">
                    <div class="adjust-label">重写章节</div>
                    <el-checkbox-group v-model="adjustForm.changedSortNos" class="adjust-section-group" @change="fitPromptHeight">
                        <div v-for="item in adjustSections" :key="item.sortNo" class="adjust-section-item"
                            :class="{ 'is-on': isAdjustSectionChanged(item.sortNo) || item.added }">
                            <div class="adjust-section-head">
                                <el-checkbox :label="item.sortNo">
                                    {{ item.sortNo }}. {{ item.added ? "新章节" : item.title }}
                                </el-checkbox>
                                <el-button type="text" @click.stop="removeAdjustSection(item.sortNo)">删除章节</el-button>
                            </div>
                            <div v-if="item.added" class="prompt-row">
                                <div class="prompt-label">章节标题</div>
                                <el-input v-model.trim="item.title" size="small" maxlength="80" placeholder="请输入章节标题" />
                            </div>
                            <template v-if="item.added || isAdjustSectionChanged(item.sortNo)">
                                <div class="prompt-row prompt-row-top">
                                    <div class="prompt-label">章节提示词</div>
                                    <el-input v-model="item.prompt" type="textarea" :autosize="{ minRows: 2 }" resize="none"
                                        placeholder="将按此重新生成正文"
                                        class="adjust-prompt-input" />
                                </div>
                            </template>
                        </div>
                    </el-checkbox-group>
                    <el-button size="mini" icon="el-icon-plus" class="adjust-add" @click="addAdjustSection">添加章节</el-button>
                </div>
            </div>
            <span slot="footer">
                <el-button @click="adjustVisible = false">取消</el-button>
                <el-button type="primary" :loading="adjusting" @click="submitAdjust">保存为新版本</el-button>
            </span>
        </el-dialog>

        <el-dialog title="选择发布版本" :visible.sync="publishVisible" width="440px"
            custom-class="publish-version-dialog">
            <el-radio-group v-model="publishVersionId" class="version-radios">
                <el-radio v-for="item in versions" :key="item.id" :label="item.id">
                    {{ versionOptionLabel(item) }}
                    <el-tag v-if="item.published" size="mini" type="success">当前已发布</el-tag>
                </el-radio>
            </el-radio-group>
            <span slot="footer">
                <el-button @click="publishVisible = false">取消</el-button>
                <el-button type="primary" :loading="publishing" @click="submitPublish">确认发布所选版本</el-button>
            </span>
        </el-dialog>

        <el-dialog title="导出报告" :visible.sync="exportVisible" width="480px" custom-class="export-report-dialog">
            <el-form label-width="88px" size="small">
                <el-form-item label="导出格式">
                    <el-radio-group v-model="exportFormat">
                        <el-radio label="docx">Word (.docx)</el-radio>
                        <el-radio label="pdf">PDF</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <p class="field-hint">按当前所选版本导出。标题、列表和表格按版式导出，表格不是纯文本。只有一份时直接下载；个人报告有多人时，每人一份并打包为 zip。</p>
            <span slot="footer">
                <el-button @click="exportVisible = false">取消</el-button>
                <el-button type="primary" :loading="exporting" @click="submitExport">确认导出</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import DOMPurify from "dompurify";
import { saveAs } from "file-saver";
import { toMarkdownHtml } from "./markdown";
import {
    SOURCE_LABELS,
    SOURCE_ORDER,
    emptyScopeForm,
    formSourceLabel,
    formToScope,
    formatScope,
    genStatusTag,
    memberStatusLabel,
    scopeSourceLabel,
    scopeToForm,
} from "./shared";

const emptyTask = () => ({
    taskId: null,
    status: "",
    total: 0,
    done: 0,
    percent: 0,
    current: "",
    errorMsg: "",
    logs: [],
});

export default {
    name: "reportV2Wizard",
    data() {
        return {
            pageLoading: false,
            saving: false,
            generating: false,
            previewLoading: false,
            contentSaving: false,
            adjusting: false,
            publishing: false,
            exporting: false,
            step: 1,
            maxStep: 1,
            reportId: null,
            report: null,
            templates: [],
            scopeOptions: {
                stages: [],
                userGroups: [],
                areas: [],
                cycles: [],
                homeworks: [],
                logs: [],
                surveys: [],
                reports: [],
                attendanceDates: [],
            },
            form: {
                name: "",
                templateId: null,
                aiProvider: "",
                dataType: "gsq",
                category: "",
            },
            scopeForm: emptyScopeForm(),
            prepareTask: emptyTask(),
            generateTask: emptyTask(),
            prepareDone: false,
            versions: [],
            versionId: null,
            members: [],
            activeUserId: null,
            sections: [],
            systemPrompt: "",
            templateDrafts: {},
            editorVisible: false,
            editorTemplateId: null,
            editorForm: {
                systemPrompt: "",
                outputRule: "",
                sections: [],
            },
            pollToken: 0,
            adjustOriginPrompt: "",
            adjustOriginRule: "",
            adjustVisible: false,
            deletingVersion: false,
            adjustOnlySortNo: null,
            adjustOriginSortNos: [],
            adjustSections: [],
            adjustForm: {
                versionId: null,
                aiProvider: "",
                note: "",
                systemPrompt: "",
                outputRule: "",
                changedSortNos: [],
            },
            publishVisible: false,
            publishVersionId: null,
            exportVisible: false,
            exportFormat: "docx",
            contentSourceKeys: {},
            aiOptions: [],
            stepList: [
                { no: 1, title: "选择报告模板" },
                { no: 2, title: "选择数据范围" },
                { no: 3, title: "数据处理" },
                { no: 4, title: "生成与调整报告" },
            ],
        };
    },
    computed: {
        projectId() {
            return this.$store.state.user.currentProject.id;
        },
        mode() {
            return this.$route.query.mode || "create";
        },
        readonly() {
            return this.mode === "view";
        },
        scopeLocked() {
            const status = this.report && this.report.genStatus;
            return status === "preparing" || status === "generating";
        },
        configLocked() {
            return this.readonly || this.scopeLocked;
        },
        showRule() {
            return String(this.$route.query.showRule || "") === "1";
        },
        modeLabel() {
            if (this.mode === "view") return "查看报告";
            if (this.mode === "edit") return "编辑报告";
            return "新建报告";
        },
        canLeaveStep1() {
            return !!(this.form.name && this.form.templateId);
        },
        visibleCycles() {
            const cycles = this.scopeOptions.cycles || [];
            if (this.scopeForm.stageAll) return cycles;
            const keys = {};
            (this.scopeForm.stageIds || []).forEach(id => {
                keys[String(id)] = true;
            });
            if (!Object.keys(keys).length) return [];
            return cycles.filter(item => keys[String(item.stageId)]);
        },
        prepareScopeText() {
            return formatScope(formToScope(this.scopeForm, this.scopeOptions), this.scopeOptions);
        },
        scopeSourceText() {
            return scopeSourceLabel(formToScope(this.scopeForm, this.scopeOptions), this.scopeOptions);
        },
        sourceTabs() {
            return SOURCE_ORDER.map(code => ({ code, label: SOURCE_LABELS[code] }));
        },
        selectedSources() {
            return SOURCE_ORDER.filter(code => (this.scopeForm.dataSources || []).indexOf(code) >= 0);
        },
        /** 引用报告不能选自己 */
        reportChoices() {
            return (this.scopeOptions.reports || []).filter(item =>
                !this.reportId || String(item.id) !== String(this.reportId));
        },
        currentTemplateName() {
            const found = this.templates.find(item => item.id === this.form.templateId);
            return (found && found.name) || (this.report && this.report.templateName) || "报告";
        },
        canGenerate() {
            if (this.readonly) return this.maxStep >= 4;
            if (this.report && (this.report.genStatus === "done" || this.report.genStatus === "generating")) return true;
            return this.prepareDone && this.prepareTask.status !== "running" && this.prepareTask.status !== "pending";
        },
        generateBtnText() {
            if (this.report && (this.report.genStatus === "done" || this.report.genStatus === "generating" || this.report.genStatus === "failed") && this.maxStep >= 4) {
                return "进入报告";
            }
            return "生成报告";
        },
        canEditContent() {
            return !this.readonly && this.versionId && !this.isGenerateBusy;
        },
        canOpenAdjust() {
            return !this.readonly && !!this.versionId && !this.generating && !this.isGenerateBusy;
        },
        isGenerateBusy() {
            return this.generateTask.status === "running" || this.generateTask.status === "pending";
        },
        hasRetryableFailures() {
            const retryable = status => status === "failed" || status === "pending";
            if ((this.members || []).some(item => retryable(item.genStatus))) return true;
            if ((this.sections || []).some(item => retryable(item.genStatus))) return true;
            return false;
        },
        /** 停止/失败，或任务已收尾但仍有失败/待生成项时，可继续 */
        canContinueGenerate() {
            if (this.readonly || this.isGenerateBusy) return false;
            const status = this.generateTask.status;
            if (status === "stopped" || status === "failed") return true;
            if (this.report && this.report.genStatus === "failed") return true;
            return this.hasRetryableFailures;
        },
    },
    mounted() {
        this.init();
    },
    beforeDestroy() {
        this.pollToken += 1;
    },
    methods: {
        genTag(status) {
            return genStatusTag(status);
        },
        memberLabel(item) {
            return memberStatusLabel(item.genStatus, item.genStatusLabel);
        },
        sectionKey(item) {
            return `${item.userId || "all"}-${item.sortNo}`;
        },
        isAdjustSectionChanged(sortNo) {
            return this.adjustForm.changedSortNos.indexOf(sortNo) >= 0;
        },
        contentSourceOn(item) {
            return this.canEditContent && !!this.contentSourceKeys[this.sectionKey(item)];
        },
        toggleContentSource(item) {
            const key = this.sectionKey(item);
            this.$set(this.contentSourceKeys, key, !this.contentSourceKeys[key]);
        },
        renderMarkdown(content) {
            return DOMPurify.sanitize(toMarkdownHtml(content || ""));
        },
        versionOptionLabel(item) {
            const label = item.label || `版本${item.versionNo || item.id}`;
            return item.published ? `${label}（已发布）` : label;
        },
        progressStatus(status) {
            if (status === "done") return "success";
            if (status === "failed") return "exception";
            return undefined;
        },
        logTag(status) {
            if (status === "done") return "success";
            if (status === "failed") return "danger";
            if (status === "stopped") return "info";
            return "warning";
        },
        logStatusText(status) {
            const map = { running: "进行中", done: "已完成", failed: "失败", stopped: "已停止" };
            return map[status] || status || "";
        },
        backToList() {
            this.pollToken += 1;
            this.$router.push({ path: "/home/totalReportV2" });
        },
        pickTemplate(item) {
            if (this.configLocked) return;
            this.form.templateId = item.id;
            this.form.category = item.category || "";
            this.ensureDraft(item);
        },
        cardSections(item) {
            const draft = this.templateDrafts[item.id];
            if (draft && draft.sections) return draft.sections;
            return item.sections || [];
        },
        ensureDraft(item) {
            if (!item || this.templateDrafts[item.id]) return;
            this.$set(this.templateDrafts, item.id, this.cloneOutline(item.systemPrompt, item.sections, item.outputRule));
        },
        cloneOutline(systemPrompt, sections, outputRule) {
            return {
                systemPrompt: systemPrompt || "",
                outputRule: outputRule || "",
                sections: (sections || []).map(sec => ({
                    title: sec.title || "",
                    content: sec.content || "",
                })),
            };
        },
        openTemplateEditor(item) {
            if (!this.configLocked) this.pickTemplate(item);
            this.ensureDraft(item);
            const draft = this.templateDrafts[item.id] || this.cloneOutline(item.systemPrompt, item.sections, item.outputRule);
            this.editorTemplateId = item.id;
            this.editorForm = this.cloneOutline(draft.systemPrompt, draft.sections, draft.outputRule);
            this.editorVisible = true;
        },
        resetEditorScroll() {
            this.$nextTick(() => {
                const dialog = this.$el.querySelector(".editor-dialog .el-dialog__body");
                if (dialog) dialog.scrollTop = 0;
                this.fitPromptHeight();
            });
        },
        fitPromptHeight() {
            this.$nextTick(() => {
                const areas = this.$el.querySelectorAll(".editor-prompt textarea, .adjust-prompt-input textarea");
                areas.forEach(area => {
                    area.style.height = "auto";
                    area.style.height = area.scrollHeight + "px";
                });
            });
        },
        addEditorSection() {
            this.editorForm.sections.push({ title: "", content: "" });
        },
        removeEditorSection(index) {
            this.editorForm.sections.splice(index, 1);
        },
        async saveTemplateEditor() {
            const sections = (this.editorForm.sections || []).map(item => ({
                title: (item.title || "").trim(),
                content: item.content || "",
            }));
            if (!sections.length) {
                this.$message.warning("请至少保留一个章节");
                return;
            }
            if (sections.some(item => !item.title)) {
                this.$message.warning("请填写章节标题");
                return;
            }
            this.$set(this.templateDrafts, this.editorTemplateId, {
                systemPrompt: this.editorForm.systemPrompt || "",
                outputRule: this.editorForm.outputRule || "",
                sections,
            });
            this.editorVisible = false;
            if (!this.form.name) {
                this.$message.success("已记在本报告，不影响原始模板");
                return;
            }
            const ok = await this.saveDraft();
            if (ok) this.$message.success("已保存到本报告，不影响原始模板");
        },
        sourceLabel(code) {
            return SOURCE_LABELS[code] || code;
        },
        sourceSummary(code) {
            return formSourceLabel(this.scopeForm, this.scopeOptions, code);
        },
        isSourceOn(code) {
            return (this.scopeForm.dataSources || []).indexOf(code) >= 0;
        },
        focusSource(code) {
            if (this.configLocked) return;
            this.scopeForm.focusType = code;
        },
        /** 未选则加入并聚焦；已选且已聚焦则取消（至少留一个）；已选未聚焦只切换聚焦 */
        onPickSource(code) {
            if (this.configLocked) return;
            const list = this.scopeForm.dataSources || [];
            const on = list.indexOf(code) >= 0;
            if (on && this.scopeForm.focusType === code) {
                if (list.length <= 1) {
                    this.$message.warning("请至少保留一个数据维度");
                    return;
                }
                const rest = SOURCE_ORDER.filter(item => list.indexOf(item) >= 0 && item !== code);
                this.scopeForm.dataSources = rest;
                this.scopeForm.focusType = rest[0];
                return;
            }
            if (!on) {
                this.scopeForm.dataSources = SOURCE_ORDER.filter(item => item === code || list.indexOf(item) >= 0);
            }
            this.scopeForm.focusType = code;
        },
        scopeKind(kind) {
            if (kind === "stage") return { all: "stageAll", ids: "stageIds", list: this.scopeOptions.stages || [] };
            if (kind === "group") return { all: "groupAll", ids: "userGroupIds", list: this.scopeOptions.userGroups || [] };
            if (kind === "area") return { all: "areaAll", ids: "areas", list: (this.scopeOptions.areas || []).map(name => ({ id: name })) };
            if (kind === "cycle") return { all: "cycleAll", ids: "cycleIds", list: this.visibleCycles || [] };
            return { all: "homeworkAll", ids: "homeworkIds", list: this.scopeOptions.homeworks || [] };
        },
        onToggleAll(kind) {
            const spec = this.scopeKind(kind);
            this.scopeForm[spec.ids] = this.scopeForm[spec.all] ? spec.list.map(item => item.id) : [];
            if (kind === "stage") this.syncCycleChecks();
        },
        onToggleItem(kind) {
            const spec = this.scopeKind(kind);
            const selected = this.scopeForm[spec.ids] || [];
            this.scopeForm[spec.all] = spec.list.length > 0 && selected.length === spec.list.length;
            if (kind === "stage") this.syncCycleChecks();
        },
        syncCycleChecks() {
            const visible = (this.visibleCycles || []).map(item => item.id);
            const allow = {};
            visible.forEach(id => {
                allow[String(id)] = id;
            });
            if (this.scopeForm.cycleAll) {
                this.scopeForm.cycleIds = visible.slice();
                return;
            }
            this.scopeForm.cycleIds = (this.scopeForm.cycleIds || [])
                .filter(id => allow[String(id)] !== undefined)
                .map(id => allow[String(id)]);
            this.scopeForm.cycleAll = visible.length > 0 && this.scopeForm.cycleIds.length === visible.length;
        },
        fillScopeChecks() {
            ["stage", "group", "area", "homework", "cycle"].forEach(kind => {
                const spec = this.scopeKind(kind);
                if (this.scopeForm[spec.all]) {
                    this.scopeForm[spec.ids] = spec.list.map(item => item.id);
                }
            });
        },
        async init() {
            this.pageLoading = true;
            try {
                await this.loadMeta();
                const id = this.$route.query.id;
                if (id) {
                    this.reportId = Number(id);
                    await this.loadReport(this.reportId);
                    await this.enterByStatus();
                }
            } finally {
                this.pageLoading = false;
            }
        },
        async loadMeta() {
            const projectId = this.projectId;
            const [tplRes, scopeRes, modelRes] = await Promise.all([
                this.$Api.ReportV2.getTemplates({ projectId }),
                this.$Api.ReportV2.getScopeOptions({ projectId }),
                this.$Api.ReportV2.getModels().catch(() => ({ code: -1, msg: "模型列表加载失败" })),
            ]);
            if (tplRes && tplRes.code == 200) {
                const data = tplRes.data || {};
                this.templates = Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
            } else if (tplRes && tplRes.msg) {
                this.$message.error(tplRes.msg);
            }
            if (scopeRes && scopeRes.code == 200 && scopeRes.data) {
                this.scopeOptions = Object.assign({}, this.scopeOptions, scopeRes.data);
                this.fillScopeChecks();
            }
            if (modelRes && modelRes.code == 200) {
                const data = modelRes.data || {};
                const rows = Array.isArray(data.rows) ? data.rows : [];
                this.aiOptions = rows.filter(item => item && item.code);
            } else if (modelRes && modelRes.msg) {
                this.$message.error(modelRes.msg);
            }
            this.form.aiProvider = this.pickAiProvider(this.form.aiProvider);
        },
        async loadReport(id) {
            let page = 1;
            const limit = 50;
            while (page <= 20) {
                const res = await this.$Api.ReportV2.getList({
                    projectId: this.projectId,
                    page,
                    limit,
                });
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "报告加载失败");
                    return;
                }
                const data = res.data || {};
                const rows = data.rows || [];
                const found = rows.find(item => item.id == id);
                if (found) {
                    await this.applyReport(found);
                    return;
                }
                if (page * limit >= (data.total || 0) || !rows.length) break;
                page += 1;
            }
            this.$message.error("未找到该报告");
        },
        async applyReport(row) {
            this.report = row;
            this.form.name = row.name || "";
            this.form.templateId = row.templateId;
            this.form.dataType = row.dataType || "gsq";
            this.form.category = row.category || "";
            this.form.aiProvider = this.pickAiProvider(row.aiProvider);
            // 旧报告 scope 没有 dataSources，按主数据维度兜底
            this.scopeForm = scopeToForm(row.scope, this.scopeOptions, row.dataType);
            this.fillScopeChecks();
            const tpl = this.templates.find(item => item.id === row.templateId);
            if (tpl && !this.form.category) this.form.category = tpl.category;
            await this.loadReportOutline(row.templateId);
        },
        async loadReportOutline(templateId) {
            if (!this.reportId || !templateId) return;
            const res = await this.$Api.ReportV2.getOutline({ reportId: this.reportId });
            if (!res || res.code != 200 || !res.data) return;
            this.$set(this.templateDrafts, templateId, this.cloneOutline(res.data.systemPrompt, res.data.sections, res.data.outputRule));
        },
        async enterByStatus() {
            const status = this.report && this.report.genStatus;
            if (status === "preparing") {
                this.maxStep = 3;
                this.step = 3;
                this.pollProgress("prepare");
                return;
            }
            if (status === "generating" || status === "done") {
                this.prepareDone = true;
                this.maxStep = 4;
                this.step = 4;
                if (status === "generating") this.pollProgress("generate");
                await this.loadVersions();
                return;
            }
            if (status === "failed") {
                const gen = await this.fetchProgress("generate");
                if (gen && gen.taskId) {
                    this.prepareDone = true;
                    this.applyTask("generate", gen, false);
                    this.maxStep = 4;
                    this.step = 4;
                    await this.loadVersions();
                    return;
                }
                const prep = await this.fetchProgress("prepare");
                this.applyTask("prepare", prep || emptyTask(), false);
                this.maxStep = 3;
                this.step = 3;
                return;
            }
            const prep = await this.fetchProgress("prepare");
            if (prep && prep.status === "done") {
                this.prepareDone = true;
                this.applyTask("prepare", prep, false);
                this.maxStep = 3;
            } else if (this.form.templateId) {
                this.maxStep = 2;
            }
            this.step = this.mode === "view" ? this.maxStep : 1;
        },
        onStepClick(no) {
            if (no > this.maxStep) {
                this.$message.warning("请先完成前面的步骤");
                return;
            }
            if (no === 3 && (this.prepareTask.status === "running" || this.report && this.report.genStatus === "preparing")) {
                this.step = 3;
                if (this.prepareTask.status !== "done") this.pollProgress("prepare");
                return;
            }
            this.step = no;
            if (no === 4) this.loadVersions();
        },
        validateScope() {
            if (!this.scopeForm.stageAll && !(this.scopeForm.stageIds || []).length) {
                this.$message.warning("请选择报告周期");
                return false;
            }
            if (!this.scopeForm.groupAll && !(this.scopeForm.userGroupIds || []).length) {
                this.$message.warning("请选择用户组");
                return false;
            }
            if (!this.scopeForm.areaAll && !(this.scopeForm.areas || []).length) {
                this.$message.warning("请选择区域范围");
                return false;
            }
            const sources = this.scopeForm.dataSources || [];
            if (!sources.length) {
                this.$message.warning("请至少选择一个数据维度");
                return false;
            }
            // 校验失败时聚焦到出错的维度面板
            const fail = (code, message) => {
                this.scopeForm.focusType = code;
                this.$message.warning(message);
                return false;
            };
            if (sources.indexOf("gsq") >= 0) {
                if (this.scopeForm.gsqMode === "date") {
                    if (!this.scopeForm.dateStart || !this.scopeForm.dateEnd) return fail("gsq", "请选择观思问问卷时间");
                    if (this.scopeForm.dateStart > this.scopeForm.dateEnd) return fail("gsq", "观思问开始日期不能晚于结束日期");
                } else if (this.scopeForm.gsqMode === "dates") {
                    if (!(this.scopeForm.gsqDates || []).length) return fail("gsq", "请选择观思问问卷日期");
                } else if (!this.scopeForm.cycleAll && !(this.scopeForm.cycleIds || []).length) {
                    return fail("gsq", "请选择观思问周期");
                }
            }
            if (sources.indexOf("homework") >= 0 && !this.scopeForm.homeworkAll && !(this.scopeForm.homeworkIds || []).length) {
                return fail("homework", "请选择作业");
            }
            if (sources.indexOf("log") >= 0) {
                if (this.scopeForm.logMode === "dates") {
                    if (!(this.scopeForm.logDates || []).length) return fail("log", "请选择日志作答日期");
                } else if (!!this.scopeForm.logStart !== !!this.scopeForm.logEnd) {
                    return fail("log", "请选择完整的日志日期范围");
                } else if (this.scopeForm.logStart && this.scopeForm.logStart > this.scopeForm.logEnd) {
                    return fail("log", "日志开始日期不能晚于结束日期");
                }
            }
            if (sources.indexOf("survey") >= 0 && !(this.scopeForm.surveyIds || []).length) {
                return fail("survey", "请选择调研问卷");
            }
            if (sources.indexOf("report") >= 0) {
                if (!(this.scopeForm.reportIds || []).length) return fail("report", "请选择引用的报告");
                if ((this.scopeForm.reportIds || []).some(id => String(id) === String(this.reportId))) {
                    return fail("report", "不能引用当前报告本身");
                }
            }
            if (sources.indexOf("attendance") >= 0) {
                if (this.scopeForm.attendanceMode === "dates") {
                    if (!(this.scopeForm.attendanceDates || []).length) return fail("attendance", "请选择考勤日期");
                } else if (!!this.scopeForm.attendanceStart !== !!this.scopeForm.attendanceEnd) {
                    return fail("attendance", "请选择完整的考勤日期范围");
                } else if (this.scopeForm.attendanceStart && this.scopeForm.attendanceStart > this.scopeForm.attendanceEnd) {
                    return fail("attendance", "考勤开始日期不能晚于结束日期");
                }
            }
            return true;
        },
        pickAiProvider(preferred) {
            const codes = this.aiOptions.map(item => item.code);
            if (preferred && codes.indexOf(preferred) >= 0) return preferred;
            if (codes.indexOf("qwen") >= 0) return "qwen";
            return codes[0] || "";
        },
        buildSaveBody() {
            const scope = formToScope(this.scopeForm, this.scopeOptions);
            const body = {
                projectId: this.projectId,
                name: this.form.name,
                templateId: this.form.templateId,
                dataType: scope.dataSources[0],
                scope,
            };
            if (this.form.aiProvider) body.aiProvider = this.form.aiProvider;
            if (this.reportId) body.id = this.reportId;
            const draft = this.templateDrafts[this.form.templateId];
            if (draft) {
                body.systemPrompt = draft.systemPrompt || "";
                if (this.showRule) body.outputRule = draft.outputRule || "";
                body.sections = (draft.sections || []).map(item => ({
                    title: item.title,
                    content: item.content || "",
                }));
            }
            return body;
        },
        async saveDraft() {
            if (!this.canLeaveStep1) {
                this.$message.warning("请填写报告名称并选择模板");
                return false;
            }
            if (this.readonly || this.scopeLocked) return true;
            this.saving = true;
            try {
                const res = await this.$Api.ReportV2.save(this.buildSaveBody());
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "保存失败");
                    return false;
                }
                const id = typeof res.data === "object" ? res.data.id : res.data;
                if (id) this.reportId = id;
                if (this.mode === "create" && this.reportId) {
                    this.$router.replace({
                        path: "/home/totalReportV2",
                        query: { mode: "edit", id: this.reportId },
                    });
                }
                return true;
            } catch (e) {
                this.$message.error("保存失败");
                return false;
            } finally {
                this.saving = false;
            }
        },
        async goNext() {
            const ok = await this.saveDraft();
            if (!ok) return;
            this.maxStep = Math.max(this.maxStep, 2);
            this.step = 2;
        },
        async goPrepare() {
            if (this.readonly) {
                this.maxStep = Math.max(this.maxStep, 3);
                this.step = 3;
                this.pollProgress("prepare");
                return;
            }
            if (!this.validateScope()) return;
            if (this.scopeLocked) {
                this.step = 3;
                this.pollProgress("prepare");
                return;
            }
            const ok = await this.saveDraft();
            if (!ok) return;
            this.step = 3;
            this.maxStep = Math.max(this.maxStep, 3);
            await this.startPrepare(false);
        },
        async startPrepare() {
            if (!this.reportId) return;
            this.prepareDone = false;
            this.prepareTask = emptyTask();
            const res = await this.$Api.ReportV2.prepare({ reportId: this.reportId });
            if (!res || res.code != 200) {
                this.$message.error((res && res.msg) || "数据处理启动失败");
            } else if (this.report) {
                this.report.genStatus = "preparing";
            }
            this.pollProgress("prepare");
        },
        async startGenerate() {
            const genStatus = this.report && this.report.genStatus;
            if (this.readonly || genStatus === "done" || genStatus === "generating" || (genStatus === "failed" && this.maxStep >= 4)) {
                this.step = 4;
                this.maxStep = 4;
                if (genStatus === "generating") this.pollProgress("generate");
                await this.loadVersions();
                return;
            }
            if (!this.prepareDone) {
                this.$message.warning("请先完成数据处理后再生成报告");
                return;
            }
            this.generating = true;
            try {
                const res = await this.$Api.ReportV2.generate({ reportId: this.reportId });
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "生成失败");
                    return;
                }
                if (this.report) this.report.genStatus = "generating";
                this.step = 4;
                this.maxStep = 4;
                this.generateTask = emptyTask();
                this.pollProgress("generate");
            } finally {
                this.generating = false;
            }
        },
        async fetchProgress(taskType, afterId) {
            const params = { reportId: this.reportId, taskType };
            if (afterId) params.afterId = afterId;
            const res = await this.$Api.ReportV2.getProgress(params);
            if (!res || res.code != 200) return null;
            return res.data || { logs: [] };
        },
        applyTask(taskType, data, append) {
            const target = taskType === "prepare" ? this.prepareTask : this.generateTask;
            const logs = data.logs || [];
            target.taskId = data.taskId || target.taskId;
            target.status = data.status || target.status;
            target.total = data.total || 0;
            target.done = data.done || 0;
            const percent = Number(data.percent) || 0;
            target.percent = Math.min(100, Math.max(0, percent));
            target.current = data.current || "";
            target.errorMsg = data.errorMsg || "";
            target.logs = append ? target.logs.concat(logs) : logs.slice();
            if (taskType === "prepare" && target.status === "done") this.prepareDone = true;
        },
        async pollProgress(taskType) {
            if (!this.reportId) return;
            const token = ++this.pollToken;
            let afterId = null;
            let append = false;
            let emptyRetry = 0;
            let lastProgressKey = "";
            while (token === this.pollToken) {
                const data = await this.fetchProgress(taskType, afterId);
                if (token !== this.pollToken) return;
                if (!data) return;
                const logs = data.logs || [];
                const status = data.status;
                if (!data.taskId && !logs.length && !status) {
                    if (emptyRetry >= 8) return;
                    emptyRetry += 1;
                    await this.sleep(5000);
                    continue;
                }
                emptyRetry = 0;
                const progressKey = [
                    status || "",
                    data.done || 0,
                    data.percent || 0,
                    data.errorMsg || "",
                    logs.length ? logs[logs.length - 1].id : "",
                ].join("|");
                const progressChanged = progressKey !== lastProgressKey;
                lastProgressKey = progressKey;
                this.applyTask(taskType, data, append);
                if (logs.length) {
                    afterId = logs.reduce((max, item) => Math.max(max, item.id || 0), afterId || 0);
                    append = true;
                }
                if (taskType === "generate" && progressChanged && (status === "running" || status === "pending")) {
                    if (!this.versionId) await this.loadVersions();
                    else await this.loadPreview(true);
                }
                if (status === "done" || status === "failed" || status === "stopped") {
                    if (taskType === "generate") {
                        if (this.report) {
                            if (status === "done") this.report.genStatus = "done";
                            if (status === "failed") this.report.genStatus = "failed";
                        }
                        await this.loadVersions();
                        if (this.versionId) await this.loadPreview();
                    }
                    if (taskType === "prepare" && status === "done" && this.report && this.report.genStatus === "preparing") {
                        this.report.genStatus = "draft";
                    }
                    return;
                }
                await this.sleep(5000);
            }
        },
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        async stopTask(taskType) {
            const res = await this.$Api.ReportV2.stop({ reportId: this.reportId, taskType });
            if (!res || res.code != 200) {
                this.$message.error((res && res.msg) || "停止失败");
                return;
            }
            this.$message.success("已停止");
            this.pollToken += 1;
            const data = await this.fetchProgress(taskType);
            if (data) this.applyTask(taskType, data, false);
        },
        async continueTask(taskType) {
            if (taskType === "generate") {
                await this.continueGenerate();
                return;
            }
            const res = await this.$Api.ReportV2.continueTask({ reportId: this.reportId, taskType });
            if (!res || res.code != 200) {
                this.$message.error((res && res.msg) || "继续失败");
                return;
            }
            this.pollProgress(taskType);
        },
        isRetryableStatus(status) {
            return status === "failed" || status === "pending" || status === "done";
        },
        /** 已完成用「重新生成」，失败/待生成用「继续」 */
        retryLabel(status) {
            return status === "done" ? "重新生成" : "继续";
        },
        canRetryMember(item) {
            if (this.readonly || this.isGenerateBusy || this.form.category !== "personal") return false;
            return this.isRetryableStatus(item.genStatus);
        },
        canRetrySection(item) {
            if (this.readonly || this.isGenerateBusy) return false;
            return this.isRetryableStatus(item.genStatus);
        },
        sectionRetryOpts(item) {
            const opts = { sortNo: item.sortNo };
            if (this.form.category === "personal") {
                opts.userId = this.activeUserId || item.userId;
            }
            return opts;
        },
        /**
         * 继续 / 重新生成。个人类可传 userId；再传 sortNo 只重跑该人该章。
         * 指定人/章时可重跑已完成；顶部全体继续仍只跑未完成项。
         */
        async continueGenerate(opts = {}) {
            if (!this.reportId || this.isGenerateBusy) return;
            const body = { reportId: this.reportId, taskType: "generate" };
            if (this.form.category === "personal" && opts.userId) {
                body.userId = opts.userId;
            }
            if (opts.sortNo != null && opts.sortNo !== "") {
                body.sortNo = opts.sortNo;
                if (this.form.category === "personal" && !body.userId) {
                    body.userId = this.activeUserId;
                }
            }
            if (this.form.category === "personal" && body.sortNo != null && !body.userId) {
                this.$message.warning("请先选择成员");
                return;
            }
            const res = await this.$Api.ReportV2.continueTask(body);
            if (!res || res.code != 200) {
                this.$message.error((res && res.msg) || "继续失败");
                return;
            }
            if (this.report) this.report.genStatus = "generating";
            this.generateTask = Object.assign(emptyTask(), { status: "pending" });
            const scoped = body.userId || body.sortNo != null;
            this.$message.success(scoped ? "已开始按范围重新生成" : "已继续生成");
            this.pollProgress("generate");
        },
        async loadVersions() {
            if (!this.reportId) return;
            const res = await this.$Api.ReportV2.getVersions({ reportId: this.reportId });
            if (!res || res.code != 200) return;
            const data = res.data || {};
            this.versions = data.rows || [];
            if (!this.versionId && this.versions.length) {
                const newest = this.versions.reduce((max, item) => (Number(item.id) > Number(max.id) ? item : max));
                this.versionId = newest.id;
            }
            if (this.versionId) await this.loadPreview();
        },
        async loadPreview(silent) {
            if (!this.reportId || !this.versionId) {
                this.sections = [];
                this.members = [];
                this.systemPrompt = "";
                return;
            }
            if (!silent) this.previewLoading = true;
            try {
                const params = { reportId: this.reportId, versionId: this.versionId };
                if (this.form.category === "personal" && this.activeUserId) params.userId = this.activeUserId;
                const res = await this.$Api.ReportV2.getPreview(params);
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "预览加载失败");
                    return;
                }
                const data = res.data || {};
                this.systemPrompt = data.systemPrompt || "";
                this.members = data.members || [];
                if (this.form.category === "personal" && !this.activeUserId && this.members.length) {
                    this.activeUserId = this.members[0].userId;
                    if (!this.activeUserId) return;
                    await this.loadPreview();
                    return;
                }
                this.sections = (data.sections || []).map(item => ({
                    ...item,
                    prompt: item.prompt || "",
                    content: item.content || "",
                }));
            } finally {
                if (!silent) this.previewLoading = false;
            }
        },
        switchMember(userId) {
            if (this.activeUserId === userId) return;
            this.activeUserId = userId;
            this.loadPreview();
        },
        async saveContent() {
            if (!this.canEditContent) return;
            if (this.form.category === "personal" && !this.activeUserId) {
                this.$message.warning("请先选择成员");
                return;
            }
            this.contentSaving = true;
            try {
                const body = {
                    reportId: this.reportId,
                    versionId: this.versionId,
                    sections: this.sections.map(item => ({
                        sortNo: item.sortNo,
                        title: item.title,
                        content: item.content || "",
                    })),
                };
                if (this.form.category === "personal") body.userId = this.activeUserId;
                const res = await this.$Api.ReportV2.saveContent(body);
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "保存失败");
                    return;
                }
                this.$message.success("已保存到当前版本");
            } finally {
                this.contentSaving = false;
            }
        },
        async openAdjust(sortNo) {
            const current = this.versions.find(item => item.id === this.versionId) || this.versions[0];
            const versionProvider = current && current.aiProvider;
            this.adjustOnlySortNo = (sortNo != null && sortNo !== "") ? sortNo : null;
            this.adjustForm = {
                versionId: current ? current.id : this.versionId,
                aiProvider: versionProvider || this.pickAiProvider(this.form.aiProvider),
                note: "",
                systemPrompt: "",
                outputRule: "",
                changedSortNos: [],
            };
            this.adjustVisible = true;
            await this.loadAdjustSections();
            this.fitPromptHeight();
        },
        applyAdjustChecks() {
            const sortNos = this.adjustSections.map(item => item.sortNo);
            const only = this.adjustOnlySortNo;
            if (only != null && sortNos.some(item => item === only)) {
                this.adjustForm.changedSortNos = [only];
                return;
            }
            this.adjustForm.changedSortNos = sortNos.slice();
        },
        async loadAdjustSections() {
            if (!this.adjustForm.versionId) {
                this.adjustSections = [];
                return;
            }
            const res = await this.$Api.ReportV2.getPreview({
                reportId: this.reportId,
                versionId: this.adjustForm.versionId,
            });
            if (!res || res.code != 200) return;
            const sections = ((res.data && res.data.sections) || []).slice();
            const map = {};
            sections.forEach(item => {
                if (!map[item.sortNo]) {
                    map[item.sortNo] = {
                        sortNo: item.sortNo,
                        title: item.title,
                        prompt: item.prompt || "",
                    };
                }
            });
            this.adjustSections = Object.keys(map).map(key => map[key]);
            this.adjustOriginSortNos = this.adjustSections.map(item => item.sortNo);
            this.adjustOriginPrompt = (res.data && res.data.systemPrompt) || "";
            this.adjustOriginRule = (res.data && res.data.outputRule) || "";
            this.adjustForm.systemPrompt = this.adjustOriginPrompt;
            this.adjustForm.outputRule = this.adjustOriginRule;
            this.applyAdjustChecks();
            this.fitPromptHeight();
            const version = this.versions.find(item => item.id === this.adjustForm.versionId);
            this.adjustForm.aiProvider = (version && version.aiProvider) || this.pickAiProvider(this.form.aiProvider);
        },
        async deleteAdjustVersion() {
            const version = this.versions.find(item => item.id === this.adjustForm.versionId);
            if (!version) {
                this.$message.warning("请选择要删除的版本");
                return;
            }
            if (this.versions.length <= 1) {
                this.$message.warning("请至少保留一个版本");
                return;
            }
            if (version.published) {
                this.$message.warning("当前已发布版本不能删除，请先取消发布或改发其他版本");
                return;
            }
            try {
                await this.$confirm(`确定删除「${this.versionOptionLabel(version)}」吗？删除后不可恢复。`, "提示", {
                    type: "warning",
                    confirmButtonText: "删除",
                    cancelButtonText: "取消",
                });
            } catch (e) {
                return;
            }
            this.deletingVersion = true;
            try {
                const res = await this.$Api.ReportV2.deleteVersion({
                    reportId: this.reportId,
                    versionId: version.id,
                });
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "删除失败");
                    return;
                }
                if (this.versionId === version.id) {
                    this.versionId = null;
                }
                this.adjustForm.versionId = null;
                await this.loadVersions();
                const next = this.versions[0];
                if (next) {
                    this.adjustForm.versionId = next.id;
                    await this.loadAdjustSections();
                }
                this.$message.success("已删除该版本");
            } finally {
                this.deletingVersion = false;
            }
        },
        addAdjustSection() {
            const nums = (this.adjustOriginSortNos || []).concat(this.adjustSections.map(item => item.sortNo));
            const max = nums.reduce((current, value) => Math.max(current, Number(value) || 0), 0);
            const sortNo = max + 1;
            this.adjustSections.push({
                sortNo,
                title: "",
                prompt: "",
                added: true,
            });
            if (this.adjustForm.changedSortNos.indexOf(sortNo) < 0) {
                this.adjustForm.changedSortNos.push(sortNo);
            }
            this.fitPromptHeight();
        },
        removeAdjustSection(sortNo) {
            if (this.adjustSections.length <= 1) {
                this.$message.warning("请至少保留一个章节");
                return;
            }
            this.adjustSections = this.adjustSections.filter(item => item.sortNo !== sortNo);
            this.adjustForm.changedSortNos = this.adjustForm.changedSortNos.filter(item => item !== sortNo);
        },
        adjustStructureChanged() {
            const origin = this.adjustOriginSortNos || [];
            const current = this.adjustSections.map(item => item.sortNo);
            if (current.length !== origin.length) {
                return true;
            }
            return current.some(sortNo => origin.indexOf(sortNo) < 0);
        },
        async submitAdjust() {
            const version = this.versions.find(item => item.id === this.adjustForm.versionId);
            const currentProvider = (version && version.aiProvider) || "";
            const nextProvider = this.adjustForm.aiProvider || "";
            const aiChanged = !version || currentProvider !== nextProvider;
            const promptChanged = (this.adjustForm.systemPrompt || "").trim() !== (this.adjustOriginPrompt || "").trim();
            const ruleChanged = this.showRule && (this.adjustForm.outputRule || "").trim() !== (this.adjustOriginRule || "").trim();
            const structureChanged = this.adjustStructureChanged();
            if (this.adjustSections.some(item => item.added && !(item.title || "").trim())) {
                this.$message.warning("请填写新增章节标题");
                return;
            }
            if (!aiChanged && !promptChanged && !ruleChanged && !this.adjustForm.changedSortNos.length && !structureChanged) {
                this.$message.warning("未检测到修改");
                return;
            }
            this.adjusting = true;
            try {
                const body = {
                    reportId: this.reportId,
                    versionId: this.adjustForm.versionId,
                    note: this.adjustForm.note,
                    systemPrompt: (this.adjustForm.systemPrompt || "").trim(),
                    outputRule: this.showRule ? (this.adjustForm.outputRule || "") : undefined,
                    sections: this.adjustSections.map(item => {
                        const changed = item.added || this.adjustForm.changedSortNos.indexOf(item.sortNo) >= 0;
                        const row = {
                            sortNo: item.sortNo,
                            title: item.title,
                            changed,
                        };
                        // adjust 的 content 是新的章节说明（对应预览 prompt），不是正文
                        if (changed) {
                            row.content = (item.prompt || "").trim();
                        }
                        return row;
                    }),
                };
                if (nextProvider) body.aiProvider = nextProvider;
                const res = await this.$Api.ReportV2.adjust(body);
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "调整失败");
                    return;
                }
                this.adjustVisible = false;
                this.$message.success("已开始生成新版本");
                if (this.report) this.report.genStatus = "generating";
                this.versionId = null;
                this.generateTask = emptyTask();
                this.pollProgress("generate");
            } finally {
                this.adjusting = false;
            }
        },
        openPublish() {
            this.publishVersionId = this.versionId || (this.versions[0] && this.versions[0].id);
            this.publishVisible = true;
        },
        async submitPublish() {
            if (!this.publishVersionId) {
                this.$message.warning("请选择发布版本");
                return;
            }
            this.publishing = true;
            try {
                const res = await this.$Api.ReportV2.publish({
                    reportId: this.reportId,
                    versionId: this.publishVersionId,
                });
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "发布失败");
                    return;
                }
                this.$message.success("已发布");
                this.publishVisible = false;
                if (this.report) this.report.publishStatus = "published";
                await this.loadVersions();
            } finally {
                this.publishing = false;
            }
        },
        async submitExport() {
            if (!this.versionId) return;
            this.exporting = true;
            try {
                const res = await this.$Api.ReportV2.exportReport({
                    reportId: this.reportId,
                    versionId: this.versionId,
                    format: this.exportFormat,
                });
                if (!res || res.code != 200) {
                    this.$message.error((res && res.msg) || "导出失败");
                    return;
                }
                const taskId = typeof res.data === "object" ? res.data.taskId || res.data.id : res.data;
                const file = await this.waitExport(taskId);
                await this.downloadFile(taskId, file.fileName);
                this.exportVisible = false;
                this.$message.success("导出完成");
            } catch (e) {
                this.$message.error((e && e.message) || "导出失败");
            } finally {
                this.exporting = false;
            }
        },
        async waitExport(taskId) {
            const start = Date.now();
            while (Date.now() - start < 15 * 60 * 1000) {
                const res = await this.$Api.ReportV2.getExportStatus({ taskId });
                if (!res || res.code != 200) throw new Error((res && res.msg) || "导出失败");
                const data = res.data || {};
                if (data.status === "done") return data;
                if (data.status === "failed" || data.status === "stopped") {
                    throw new Error(data.errorMsg || "导出失败");
                }
                await this.sleep(2000);
            }
            throw new Error("导出超时，请稍后重试");
        },
        async downloadFile(taskId, fileName) {
            const res = await this.$Api.ReportV2.download({ taskId });
            const blob = res && res.data;
            if (blob && blob.type && blob.type.indexOf("application/json") >= 0) {
                const text = await blob.text();
                const json = JSON.parse(text);
                throw new Error((json && json.msg) || "下载失败");
            }
            const fallback = `${this.form.name || "报告"}.${this.exportFormat === "pdf" ? "pdf" : "docx"}`;
            saveAs(blob, fileName || fallback);
        },
    },
};
</script>

<style lang="less" scoped>
@import "../../manage/less/table.less";

.totalReport {
    padding: 20px 25px 40px;
    box-sizing: border-box;
}

.wizard-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
}

.mode-label {
    margin-left: auto;
    color: #909399;
    font-size: 13px;
}

.wizard-steps {
    margin-bottom: 16px;
    cursor: pointer;
}

.wizard-card {
    background: #fff;
    border-radius: 8px;
    padding: 18px 20px 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.scope-checks {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.scope-items {
    display: contents;
}

.scope-checks /deep/ .el-checkbox {
    margin-right: 18px;
    margin-bottom: 4px;
}

.source-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.source-tab {
    padding: 4px 14px;
    border: 1px solid #dcdfe6;
    border-radius: 14px;
    background: #fff;
    color: #606266;
    font-size: 13px;
    line-height: 20px;
    cursor: pointer;
    outline: none;

    &:hover {
        border-color: #409eff;
        color: #409eff;
    }

    &.is-on {
        border-color: #409eff;
        color: #409eff;
        background: #ecf5ff;
    }

    &.is-focus {
        border-color: #409eff;
        background: #409eff;
        color: #fff;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

.selected-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.selected-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 10px;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    background: #fafafa;
    font-size: 12px;
    line-height: 20px;
    cursor: pointer;

    b {
        color: #303133;
        font-weight: 600;
    }

    em {
        color: #909399;
        font-style: normal;
    }

    &.is-focus {
        border-color: #409eff;
        background: #ecf5ff;
    }
}

.list-checks {
    display: flex;
    flex-direction: column;
    gap: 4px;

    /deep/ .el-checkbox {
        margin-right: 0;
        margin-bottom: 0;
    }
}

.check-meta {
    margin-left: 6px;
    color: #909399;
    font-size: 12px;
}

.card-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.card-hint,
.field-hint {
    font-size: 12px;
    color: #909399;
    font-weight: 400;
}

.field-hint {
    margin: 6px 0 0;
}

.base-form {
    max-width: 100%;
}

.tpl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 12px;
}

.tpl-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    background: #fff;

    &.active {
        border-color: #409eff;
        background: #ecf5ff;
    }

    &.disabled {
        cursor: not-allowed;
    }
}

.tpl-name {
    font-weight: 600;
    margin-bottom: 6px;
}

.tpl-desc,
.tpl-sec {
    margin: 8px 0 0;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
}

.tpl-sections {
    margin: 8px 0 0;
    padding-left: 18px;
    color: #606266;
    font-size: 13px;
    line-height: 1.8;
}

.tpl-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
    color: #909399;
    font-size: 12px;
}

.editor-section {
    max-width: 640px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #ebeef5;
}

.editor-section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    color: #606266;
}

.prompt-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
}

.prompt-row-top {
    align-items: flex-start;
}

.prompt-row .el-input,
.prompt-row .el-textarea {
    flex: 1;
    min-width: 0;
}

.prompt-label {
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    line-height: 32px;
    white-space: nowrap;
    color: #303133;
}

.prompt-row-top .prompt-label {
    line-height: 32px;
}

.editor-prompt /deep/ .el-textarea__inner,
.adjust-prompt-input /deep/ .el-textarea__inner {
    overflow: hidden;
}

.tpl-sec-title {
    font-size: 12px;
    color: #606266;
    font-weight: 600;
}

.tpl-sec-desc {
    margin-top: 2px;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
}

.scroll-text /deep/ .el-textarea__inner {
    overflow-y: auto;
}

.scroll-text-view {
    max-height: 480px;
    overflow-y: auto;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    line-height: 1.7;
}

.md-view {
    white-space: normal;
    color: #303133;

    /deep/ h1,
    /deep/ h2,
    /deep/ h3,
    /deep/ h4 {
        margin: 0.6em 0 0.3em;
        font-weight: 600;
        line-height: 1.4;
    }

    /deep/ h1 { font-size: 18px; }
    /deep/ h2 { font-size: 16px; }
    /deep/ h3,
    /deep/ h4 { font-size: 15px; }

    /deep/ p {
        margin: 0.4em 0;
    }

    /deep/ ul,
    /deep/ ol {
        margin: 0.4em 0;
        padding-left: 1.4em;
    }

    /deep/ ul {
        list-style: disc;
    }

    /deep/ ol {
        list-style: decimal;
    }

    /deep/ li {
        display: list-item;
        list-style: inherit;
    }

    /deep/ table {
        display: table;
        width: 100%;
        margin: 8px 0;
        border-collapse: collapse;
        border-spacing: 0;
    }

    /deep/ thead {
        display: table-header-group;
    }

    /deep/ tbody {
        display: table-row-group;
    }

    /deep/ tr {
        display: table-row;
    }

    /deep/ th,
    /deep/ td {
        display: table-cell;
        border: 1px solid #dcdfe6;
        padding: 6px 8px;
        text-align: left;
        vertical-align: top;
        font-weight: normal;
    }

    /deep/ th {
        background: #f5f7fa;
        font-weight: 600;
    }

    /deep/ pre {
        margin: 0.4em 0;
        padding: 8px;
        overflow: auto;
        background: #f5f7fa;
        border-radius: 4px;
    }
}

.system-prompt-box {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;
}

.bottom-bar {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #ebeef5;
}

.date-sep {
    margin: 0 8px;
    color: #909399;
}

.progress-head {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
}

.progress-meta {
    margin: 8px 0 12px;
    color: #606266;
    font-size: 13px;
}

.log-panels {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 12px;
}

.log-panel {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    min-height: 180px;
}

.log-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.log-current {
    color: #606266;
    line-height: 1.6;
}

.log-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 280px;
    overflow: auto;

    li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 6px 0;
        border-bottom: 1px solid #f2f3f5;
        font-size: 13px;
    }
}

.log-time {
    margin-left: auto;
    color: #c0c4cc;
    white-space: nowrap;
}

.gen-progress {
    margin-bottom: 12px;
}

.preview-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.version-label {
    color: #606266;
    font-size: 13px;
}

.preview-actions {
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.preview-layout {
    display: block;

    &.is-personal {
        display: grid;
        grid-template-columns: 220px 1fr;
        gap: 12px;
    }
}

.member-side {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 8px;
    max-height: 640px;
    overflow: auto;
}

.member-head {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
}

.member-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 6px;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;

    &.active {
        background: #ecf5ff;
        color: #409eff;
    }
}

.member-main {
    min-width: 0;
    flex: 1;
}

.member-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.member-name {
    word-break: break-all;
}

.member-retry {
    flex-shrink: 0;
    padding: 0;
}

.fail-reason {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.4;
    color: #f56c6c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
}

.section-block {
    margin-bottom: 16px;
}

.section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-weight: 600;
    flex-wrap: wrap;
}

.section-ops {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.section-fail {
    margin-bottom: 8px;
}

.section-prompt {
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f5f7fa;
    border-radius: 6px;
    color: #606266;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre-wrap;

    &.is-empty {
        color: #c0c4cc;
    }
}

/deep/ .editor-dialog .el-dialog__body,
/deep/ .adjust-dialog .el-dialog__body {
    display: block;
    max-height: calc(100vh - 170px);
    overflow: auto;
    padding: 8px 20px 4px;
}

/deep/ .editor-dialog .el-dialog__body > .editor-form {
    width: 100%;
}

.editor-form /deep/ .el-form-item__label {
    padding-bottom: 4px;
    line-height: 1.4;
}

.editor-form /deep/ .el-form-item {
    margin-bottom: 12px;
}

.adjust-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.adjust-meta {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.9fr) minmax(0, 1.2fr);
    gap: 12px 16px;
}

.adjust-label {
    margin-bottom: 6px;
    font-size: 13px;
    line-height: 1;
    color: #606266;
}

.adjust-version-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .el-select {
        flex: 1;
        min-width: 0;
    }
}

.adjust-section-group {
    width: 100%;
    max-height: 280px;
    overflow: auto;
    border: 1px solid #ebeef5;
    border-radius: 8px;
}

.adjust-section-item {
    padding: 10px 12px;
    border-bottom: 1px solid #f2f3f5;

    &:last-child {
        border-bottom: none;
    }

    &.is-on {
        background: #f5f9ff;
    }
}

.adjust-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.adjust-add {
    margin-top: 10px;
}

.adjust-prompt-input {
    display: block;
    margin-top: 8px;
}

/deep/ .publish-version-dialog .el-dialog__body,
/deep/ .export-report-dialog .el-dialog__body {
    display: block;
    align-items: stretch;
    justify-content: flex-start;
}

/deep/ .publish-version-dialog .el-dialog__body {
    max-height: 420px;
    overflow: auto;
    padding-top: 8px;
}

.version-radios {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
}

.version-radios /deep/ .el-radio {
    margin-right: 0;
    line-height: 22px;
    white-space: normal;
}

@media (max-width: 768px) {
    .log-panels,
    .preview-layout.is-personal {
        grid-template-columns: 1fr;
    }
}
</style>
